import { fetchClinVarBatch } from './clinvarDatabase-API';
const API_URL = process.env.REACT_APP_API_URL || 'https://geneie513-api-449519797657.us-central1.run.app';

export const parseGeneticFile = (content) => {
  const lines = content.split('\n');
  const snps = [];
  
  for (let line of lines) {
    // Skip comments and headers
    if (line.startsWith('#') || line.startsWith('rsid')) continue;
    
    const parts = line.trim().split(/\s+/);
    if (parts.length >= 4) {
      const rsid = parts[0];
      const chromosome = parts[1];
      const position = parts[2];
      const genotype = parts[3];
      
      if (rsid.startsWith('rs') && genotype !== '--') {
        snps.push({ rsid, chromosome, position, genotype });
      }
    }
  }
  
  return snps;
};

/**
 * Analyze SNPs with session-based caching support.
 *
 * Key changes from previous version:
 * 1. All parsed SNPs are merged with ClinVar data (not just matched ones).
 *    SNPs with no ClinVar entry are still included so GWAS can enrich them.
 * 2. GWAS enrichment runs on ALL SNPs, not just magnitude >= 2 hits.
 *    The magnitude filter was a workaround for the old per-SNP EBI API;
 *    the Postgres batch endpoint makes it unnecessary.
 * 3. Partial results are emitted after ClinVar merge (before GWAS) so the
 *    UI can show something immediately while GWAS loads.
 */
export const analyzeSNPs = async (snps, options = {}) => {
  const { 
    sessionId, 
    onClinVarProgress, 
    onGWASProgress, 
    onPartialResults,
    includeGWAS = false 
  } = options;
  
  console.log(`📊 Analyzing ${snps.length.toLocaleString()} SNPs...`);
  
  const rsids = snps.map(snp => snp.rsid);
  
  const { database: clinvarDatabase, sessionId: returnedSessionId } = await fetchClinVarBatch(
    rsids, 
    onClinVarProgress,
    sessionId
  );

  // Merge ClinVar data onto ALL SNPs (not just matched ones)
  // SNPs without a ClinVar entry are kept so GWAS enrichment can still fire
  const merged = snps.map(snp => {
    const normalizedRsid = snp.rsid.toLowerCase();
    const dbEntry = clinvarDatabase[normalizedRsid];

    if (!dbEntry) {
      return { ...snp, sessionId: returnedSessionId };
    }

    const variantInfo = dbEntry.variants['general'] || dbEntry.variants[snp.genotype];

    if (!variantInfo) {
      return { ...snp, gene: dbEntry.gene || snp.rsid, sessionId: returnedSessionId };
    }

    return {
      ...snp,
      gene: dbEntry.gene || snp.rsid,
      ...variantInfo,
      sessionId: returnedSessionId
    };
  });

  const clinvarHits = merged.filter(s => s.magnitude != null);
  console.log(`✅ Found ${clinvarHits.length.toLocaleString()} SNPs in ClinVar database`);

  if (!includeGWAS) {
    if (onPartialResults && clinvarHits.length > 0) {
      onPartialResults(clinvarHits);
    }
    return clinvarHits.sort((a, b) => (b.magnitude || 0) - (a.magnitude || 0));
  }

  // Emit ClinVar-only partial results immediately so UI isn't blank during GWAS
  if (onPartialResults && clinvarHits.length > 0) {
    onPartialResults(clinvarHits);
  }

  console.log(`🧬 Starting GWAS enrichment for all ${merged.length.toLocaleString()} SNPs...`);

  try {
    const enriched = await enrichWithGWAS(merged, onGWASProgress);

    const gwasHits = enriched.filter(s => s.gwas?.hasData).length;
    console.log(`✅ GWAS enrichment complete: ${gwasHits.toLocaleString()} variants enriched`);

    // Return only SNPs that have ClinVar data OR GWAS data
    const results = enriched.filter(s => s.magnitude != null || s.gwas?.hasData);

    console.log(`📊 Final result set: ${results.length.toLocaleString()} SNPs with ClinVar or GWAS data`);

    // Sort by magnitude (ClinVar) first; for GWAS-only SNPs use the best
    // carried userEffectiveOdds deviation from 1.0 as a proxy magnitude so
    // they are interleaved with ClinVar results rather than sinking to the bottom.
    return results.sort((a, b) => {
      const getMagnitude = (s) => {
        if (s.magnitude != null) return s.magnitude;
        const bestAssoc = s.gwas?.topAssociations?.find(x =>
          !x.gxe &&
          (x.copiesCarried === null || x.copiesCarried > 0) &&
          x.userEffectiveOdds >= 0.01 && x.userEffectiveOdds <= 100
        );
        if (!bestAssoc) return 0;
        const odds = bestAssoc.userEffectiveOdds;
        // Map OR to a 0–5 magnitude-like scale comparable to ClinVar entries:
        // OR ≥ 5  → 4,  OR ≥ 2 → 3,  OR ≥ 1.5 → 2,  OR > 1 → 1
        // Inverse for protective (OR < 1)
        const dev = odds > 1 ? odds : 1 / odds;
        if (dev >= 5)   return 4;
        if (dev >= 2)   return 3;
        if (dev >= 1.5) return 2;
        if (dev > 1)    return 1;
        return 0;
      };
      return getMagnitude(b) - getMagnitude(a);
    });
  } catch (err) {
    console.warn('⚠️ GWAS enrichment failed, returning ClinVar-only results:', err);
    return clinvarHits.sort((a, b) => (b.magnitude || 0) - (a.magnitude || 0));
  }
};

/**
 * Enrich variants with GWAS data via the backend Postgres batch endpoint.
 * Accepts any array of SNPs (with or without ClinVar data).
 */
const enrichWithGWAS = async (snps, onProgress) => {
  console.log(`🧬 GWAS enrichment: ${snps.length.toLocaleString()} SNPs`);
  if (snps.length === 0) return snps;

  const BATCH_SIZE = 5000;
  const rsids = snps.map(s => s.rsid);
  const allGwasData = {};

  try {
    const batches = [];
    for (let i = 0; i < rsids.length; i += BATCH_SIZE) {
      batches.push(rsids.slice(i, i + BATCH_SIZE));
    }

    console.log(`📦 Split into ${batches.length} batches of up to ${BATCH_SIZE.toLocaleString()} SNPs each`);

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      
      console.log(`📤 GWAS batch ${i + 1}/${batches.length}: ${batch.length.toLocaleString()} SNPs`);

      const response = await fetch(`${API_URL}/api/gwas/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rsids: batch })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ GWAS batch ${i + 1} failed:`, {
          status: response.status,
          body: errorText,
          batchSize: batch.length
        });
        continue;
      }

      const { gwas } = await response.json();
      Object.assign(allGwasData, gwas);

      const hits = Object.keys(gwas).length;
      console.log(`✅ GWAS batch ${i + 1}/${batches.length}: ${hits.toLocaleString()} variants with associations`);

      if (onProgress) {
        const progress = Math.round(((i + 1) / batches.length) * 100);
        onProgress(progress, Object.keys(allGwasData).length);
      }
    }

    console.log(`✅ GWAS complete: ${Object.keys(allGwasData).length.toLocaleString()} total variants enriched`);

    return snps.map(snp => buildEnrichedSnp(snp, allGwasData));

  } catch (err) {
    console.error('❌ GWAS enrichment failed:', {
      error: err.message,
      stack: err.stack,
      snpsCount: snps.length
    });
    return snps;
  }
};

/**
 * Merge raw GWAS API response onto a single SNP object.
 *
 * Handles:
 * - Allele matching against user genotype
 * - OR direction correction (named allele may be protective, not risk)
 * - gxe flag passed through from API so frontend can show conditional note
 * - ancestries array passed through for future population filter
 * - copiesCarried=null when riskAllele is unparseable — "unknown", not "zero"
 */
const buildEnrichedSnp = (snp, allGwasData) => {
  const gwasData = allGwasData[snp.rsid.toLowerCase()];
  if (!gwasData) return snp;

  const associations = gwasData.associations || [];
  if (associations.length === 0) return snp;

  const userAlleles = snp.genotype ? snp.genotype.toUpperCase().split('') : [];

  // Deduplicated plain-English trait names (all associations, not just carried)
  const traits = [...new Set(
    associations
      .map(a => a.trait)
      .filter(t => t && !t.startsWith('http') && t !== 'Unknown trait')
      .map(t => t.replace(/\s*\([^)]*inv[.-]norm[^)]*\)/gi, '').trim())
      .filter(Boolean)
  )];

  // Sort: prioritise associations matching user's alleles, then by OR deviation
  const sortedAssociations = [...associations].sort((a, b) => {
    const aAllele = a.riskAllele?.split('-')[1];
    const bAllele = b.riskAllele?.split('-')[1];
    const aMatches = aAllele ? userAlleles.filter(u => u === aAllele.toUpperCase()).length : 0;
    const bMatches = bAllele ? userAlleles.filter(u => u === bAllele.toUpperCase()).length : 0;

    if (aMatches === 2 && bMatches !== 2) return -1;
    if (bMatches === 2 && aMatches !== 2) return 1;

    if (aMatches === 2 && bMatches === 2) {
      const aIsRisk = a.orBeta > 1;
      const bIsRisk = b.orBeta > 1;
      if (aIsRisk && !bIsRisk) return -1;
      if (bIsRisk && !aIsRisk) return 1;
    }

    if (aMatches > 0 && bMatches === 0) return -1;
    if (bMatches > 0 && aMatches === 0) return 1;

    return 0;
  });

  const topAssociations = sortedAssociations.map(a => {
    const allele = a.riskAllele?.split('-')[1] || null;

    // copiesCarried is null when allele is unparseable — "unknown", not "zero"
    const copiesCarried = allele
      ? userAlleles.filter(u => u === allele.toUpperCase()).length
      : null;

    let direction = null;
    let userEffectiveOdds = a.orBeta;

    if (a.orBeta && copiesCarried !== null) {
      if (a.orBeta < 1) {
        // Named allele is PROTECTIVE — having it = lower risk
        direction = copiesCarried > 0 ? 'decreased' : 'increased';
        userEffectiveOdds = copiesCarried > 0 ? a.orBeta : 1 / a.orBeta;
      } else {
        // Named allele is RISK — having it = higher risk
        direction = copiesCarried > 0 ? 'increased' : 'decreased';
        userEffectiveOdds = copiesCarried > 0 ? a.orBeta : 1 / a.orBeta;
      }
    }

    return {
      trait:             a.trait,
      mappedTrait:       a.mappedTrait,
      pValue:            a.pValue,
      orBeta:            a.orBeta,
      userEffectiveOdds,
      riskAllele:        a.riskAllele,
      pubmedId:          a.pubmedId,
      allele,
      userCarries:       copiesCarried !== null ? copiesCarried > 0 : null,
      copiesCarried,
      direction,
      // ← New fields from gwas_with_ancestry view
      gxe:              a.gxe || false,
      ancestries:       a.ancestries || [],
      initialAncestries: a.initialAncestries || null,
    };
  });

  return {
    ...snp,
    gwas: {
      hasData: true,
      gene: gwasData.gene,
      traits,
      topAssociations,
      totalAssociations: associations.length,
    }
  };
};